import { useMutation, useQuery } from '@tanstack/react-query'
import {
  BookText,
  DollarSign,
  Edit,
  Gift,
  Info,
  Save,
  Tag,
  Ticket,
  Trash,
  Trophy,
} from 'lucide-react'
import { parseCookies } from 'nookies'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'

import { deleteCampaign } from '@/api/campaigns/delete-campaign'
import { getCampaignsById } from '@/api/campaigns/get-campaign-by-id'
import {
  getCampaignsModo,
  type GetCampaignsModoResponse,
} from '@/api/campaigns/get-campaigns-modo'
import {
  getCampaignsPrazo,
  type GetCampaignsPrazoResponse,
} from '@/api/campaigns/get-campaigns-prazo'
import {
  updateCampaign,
  type UpdateCampaignBody,
} from '@/api/campaigns/update-campaign'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { env } from '@/env'
import { useHeadManager } from '@/lib/headManagerProvider'
import { api } from '@/services/axios'

import { FinanceTab } from './components/financeTab'
import { PremioCRUD } from './components/premioCRUD'
import { PromocaoCRUD } from './components/promocaoCRUD'
import { TituloPremiadoCRUD } from './components/tituloPremiadoCRUD'
import { dataURLtoFile } from './components/utils'

interface StatusToggleProps {
  active: boolean
  onToggle: () => void
  disabled?: boolean
  sorteada: boolean
}

const campaignSchema = z.object({
  titulo: z.string().min(3, 'Título deve ter no mínimo 3 caracteres.'),
  descricao: z.string().min(5, 'Descrição deve ter no mínimo 5 caracteres.'),
  titulo_valor: z
    .string()
    .refine(
      (val) => {
        const num = parseFloat(val.replace(',', '.'))
        return !isNaN(num) && num > 0
      },
      {
        message: 'Informe um valor numérico válido maior que 0.',
      },
    )
    .refine(
      (val) => {
        const parts = val.replace(',', '.').split('.')
        return parts.length < 2 || parts[1].length <= 2
      },
      {
        message: 'O valor deve ter no máximo 2 casas decimais.',
      },
    ),
  titulo_quantidade: z
    .number()
    .min(1, 'Quantidade total deve ser maior que 0.'),
  titulo_min: z.number().min(1, 'Quantidade mínima deve ser maior que 0.'),
  titulo_max: z.number().min(1, 'Quantidade máxima deve ser maior que 0.'),
  dt_sorteio: z.string().optional(),
  modo_id: z.number({ invalid_type_error: 'Selecione um modo válido.' }),
  prazo_id: z.number({ invalid_type_error: 'Selecione um prazo válido.' }),
  notificar_grupo_whatsapp: z.boolean().optional(),
  nome_grupo_whatsapp: z
    .string()
    .optional()
    .refine(
      (val) => {
        return !val || val.trim().length >= 3
      },
      {
        message: 'Nome do grupo deve ter ao menos 3 caracteres se preenchido.',
      },
    ),
})

type CampaignForm = z.infer<typeof campaignSchema>

export function CampaignManagement() {
  const { setTitle } = useHeadManager()
  const { rifaId } = useParams()
  const navigate = useNavigate()
  const cookies = parseCookies()
  const userId = cookies['reactAuth.id'] || ''
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [isEditingImage, setIsEditingImage] = useState(false)
  const [tempImage, setTempImage] = useState('')
  const [showPrazoTooltip, setShowPrazoTooltip] = useState(false)
  const [key, setKey] = useState(0) // Para forçar re-render
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [modoOptions, setModoOptions] = useState<GetCampaignsModoResponse[]>([])
  const [prazoOptions, setPrazoOptions] = useState<GetCampaignsPrazoResponse[]>(
    [],
  )
  const [showGrupoTooltip, setShowGrupoTooltip] = useState(false)
  const [allPurchesedNumbers, setAllPurchesedNumbers] = useState<number[]>([])
  const [isActive, setIsActive] = useState(false)

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign', rifaId],
    queryFn: () => getCampaignsById(Number(rifaId!)),
    enabled: !!rifaId,
  })

  useEffect(() => {
    if (campaign) {
      setIsActive(!!campaign.campanha?.status)
    }
  }, [campaign])

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm<CampaignForm>()

  const modosMutation = useMutation({
    mutationFn: getCampaignsModo,
    onSuccess: (data) => {
      setModoOptions(data)
      setKey((prev) => prev + 1) // Força re-render após carregar opções
    },
  })

  const prazosMutation = useMutation({
    mutationFn: getCampaignsPrazo,
    onSuccess: (data) => {
      setPrazoOptions(data)
      setKey((prev) => prev + 1) // Força re-render após carregar opções
    },
  })

  useEffect(() => {
    if (campaign && userId && Number(userId) !== campaign.campanha.usuario_Id) {
      navigate('/not-found', { replace: true })
    }
  }, [campaign, userId, navigate])

  useEffect(() => {
    if (campaign) {
      setValue('titulo', campaign.campanha.titulo)
      setValue('descricao', campaign.campanha.descricao)
      setValue(
        'titulo_valor',
        campaign.campanha.titulo_Valor?.toString() || '0',
        { shouldDirty: false },
      )
      setValue('titulo_quantidade', campaign.campanha.titulo_Quantidade || 0, {
        shouldDirty: false,
      })
      setValue('titulo_min', campaign.campanha.titulo_Min || 0, {
        shouldDirty: false,
      })
      setValue('titulo_max', campaign.campanha.titulo_Max || 0, {
        shouldDirty: false,
      })
      setValue('modo_id', campaign.campanha.modo_Id || 0, {
        shouldDirty: false,
      })
      setValue('prazo_id', campaign.campanha.prazo_Id || 0, {
        shouldDirty: false,
      })
      setValue(
        'notificar_grupo_whatsapp',
        campaign.campanha.notificar_grupo_whatsapp || false,
        {
          shouldDirty: false,
        },
      )
      setValue(
        'nome_grupo_whatsapp',
        campaign.campanha.nome_grupo_whatsapp || '',
        {
          shouldDirty: false,
        },
      )
      if (campaign.campanha.dt_Sorteio) {
        setValue(
          'dt_sorteio',
          new Date(campaign.campanha.dt_Sorteio).toISOString().slice(0, 16),
          { shouldDirty: false },
        )
      }
      modosMutation.mutate()
      prazosMutation.mutate()
    }
  }, [campaign, setValue])

  function StatusToggle({
    active,
    onToggle,
    disabled,
    sorteada,
  }: StatusToggleProps) {
    const label = sorteada ? 'Sorteada' : active ? 'Publicada' : 'Rascunho'

    const bgColor = sorteada
      ? 'bg-purple-500'
      : active
        ? 'bg-green-500'
        : 'bg-gray-500'

    const switchColor = active ? 'bg-green-500' : 'bg-gray-200'
    const thumbPosition = active ? 'translate-x-6' : 'translate-x-1'

    return (
      <div className="flex items-center gap-2">
        <div
          className={`flex items-center justify-center rounded-full px-3 py-1 text-xs font-medium text-white ${bgColor}`}
        >
          {label}
        </div>

        {!sorteada && (
          <button
            type="button"
            onClick={onToggle}
            disabled={disabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none ${switchColor} ${
              disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${thumbPosition}`}
            />
          </button>
        )}
      </div>
    )
  }

  const toggleStatusMutation = useMutation({
    mutationFn: async (newStatus: boolean) => {
      if (!rifaId) return
      await api.put(`/Campanha/${rifaId}/status`, { Status: newStatus })
      return newStatus
    },
    onSuccess: (newStatus) => {
      if (newStatus !== undefined) {
        setIsActive(newStatus)
        toast.success(
          `Campanha ${newStatus ? 'Publicada' : 'Arquivada'} com sucesso!`,
        )
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao alterar status da campanha')
    },
  })

  // Verifica se os valores selecionados existem nas opções
  useEffect(() => {
    if (prazoOptions.length > 0 && watch('prazo_id')) {
      const exists = prazoOptions.some((opt) => opt.id === watch('prazo_id'))
      if (!exists) setValue('prazo_id', 0)
    }

    if (modoOptions.length > 0 && watch('modo_id')) {
      const exists = modoOptions.some((opt) => opt.id === watch('modo_id'))
      if (!exists) setValue('modo_id', 0)
    }
  }, [prazoOptions, modoOptions, watch, setValue])

  const updateCampaignMutation = useMutation({
    mutationFn: async (data: CampaignForm) => {
      if (!rifaId) return

      const valorNumerico = parseFloat(data.titulo_valor.replace(',', '.')) || 0

      const updateData: UpdateCampaignBody = {
        id: Number(rifaId),
        titulo: data.titulo,
        titulo_quantidade: data.titulo_quantidade,
        titulo_valor: valorNumerico,
        descricao: data.descricao,
        prazo_id: data.prazo_id,
        titulo_min: data.titulo_min,
        titulo_max: data.titulo_max,
        modo_id: data.modo_id,
        dt_sorteio: data.dt_sorteio,
        notificar_grupo_whatsapp: data.notificar_grupo_whatsapp ?? false,
        nome_grupo_whatsapp: data.nome_grupo_whatsapp ?? '',
        imagemFile: tempImage
          ? dataURLtoFile(tempImage, 'image.jpg')
          : undefined,
      }

      await updateCampaign(updateData)
    },
    onSuccess: () => {
      toast.success('Campanha atualizada com sucesso!')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao atualizar campanha')
    },
  })

  const deleteCampaignMutation = useMutation({
    mutationFn: async () => {
      if (!rifaId) return
      await deleteCampaign(Number(rifaId))
    },
    onSuccess: () => {
      toast.success('Campanha excluída com sucesso!')
      navigate('/campanhas')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao excluir campanha')
    },
  })

  const handleDraw = async () => {
    const { data: purchesedNumbers } = await api.get<number[]>(
      `CampanhaPremio/GetAllPurchasedNumbers/${rifaId}`,
    )
    setAllPurchesedNumbers(purchesedNumbers)
    if (campaign?.campanha.sorteado) {
      navigate(`/campanhas/${rifaId}/sortear`)
    } else if (campaign?.premios.length === 0) {
      toast.warning('Você precisa cadastrar prêmios em sua campanha!!')
    } else if (
      purchesedNumbers.length === campaign?.campanha.titulo_Quantidade &&
      campaign?.premios.length > 0
    ) {
      navigate(`/campanhas/${rifaId}/sortear`)
    } else {
      toast.warning('Sua campanha ainda não vendeu todos títulos!!')
    }
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setTempImage(event.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const saveImage = () => {
    if (!campaign?.campanha) return

    const campanhaAtualizada = {
      ...campaign.campanha,
      imagem: tempImage,
    }

    updateCampaignMutation.mutate({
      titulo: campanhaAtualizada.titulo,
      descricao: campanhaAtualizada.descricao,
      titulo_valor: campanhaAtualizada.titulo_Valor?.toString() || '0',
      titulo_quantidade: campanhaAtualizada.titulo_Quantidade || 0,
      titulo_min: campanhaAtualizada.titulo_Min || 0,
      titulo_max: campanhaAtualizada.titulo_Max || 0,
      modo_id: campanhaAtualizada.modo_Id || 0,
      prazo_id: campanhaAtualizada.prazo_Id || 0,
      dt_sorteio: campanhaAtualizada.dt_Sorteio
        ? new Date(campanhaAtualizada.dt_Sorteio).toISOString().slice(0, 16)
        : undefined,
    })
    setIsEditingImage(false)
  }

  const handleShowTooltip = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current)
    }
    setShowPrazoTooltip(true)
  }

  const handleHideTooltip = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowPrazoTooltip(false)
    }, 300)
  }

  const handleTooltipClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowPrazoTooltip(!showPrazoTooltip)
  }

  const handleNumberInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: 'titulo_quantidade' | 'titulo_min' | 'titulo_max',
  ) => {
    let value = e.target.value
    value = value.replace(/\D/g, '')
    const numValue = value ? parseInt(value, 10) : 0
    const finalValue = Math.max(1, numValue)

    setValue(field, finalValue, { shouldDirty: true })
    e.target.value = finalValue.toString()
  }

  const onSubmit = (data: CampaignForm) => {
    updateCampaignMutation.mutate(data)
  }

  useEffect(() => {
    setTitle('Gerenciar')
  }, [setTitle])

  if (isLoading || modosMutation.isPending || prazosMutation.isPending) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="w-full space-y-4 lg:w-1/3">
            <Skeleton className="h-64 w-full rounded-lg sm:h-80" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="w-full space-y-6 lg:w-2/3">
            <Skeleton className="h-10 w-48" />
            <div className="flex gap-2">
              <Skeleton className="h-10 w-1/3" />
              <Skeleton className="h-10 w-1/3" />
              <Skeleton className="h-10 w-1/3" />
            </div>
            <Skeleton className="h-96 w-full rounded-lg" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    )
  }

  if (campaign && Number(userId) !== campaign.campanha.usuario_Id) {
    return null
  }

  if (!campaign) return <div>Campanha não encontrada</div>

  return (
    <div key={key} className="container mx-auto p-6">
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Lado esquerdo */}
        <div className="w-full lg:w-1/3">
          <Card className="relative">
            <img
              src={
                tempImage || `${env.VITE_API_URL}${campaign.campanha.imagem}`
              }
              alt={campaign.campanha.titulo}
              className="h-64 w-full rounded-t-lg object-cover sm:h-80"
            />
          </Card>

          <div className="flex flex-col gap-2 p-4">
            {isEditingImage ? (
              <div className="space-y-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveImage} disabled={!tempImage}>
                    Salvar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setIsEditingImage(false)
                      setTempImage('')
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => setIsEditingImage(true)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Alterar imagem
              </Button>
            )}

            <Button className="w-full" onClick={handleDraw}>
              <Trophy className="mr-2 h-4 w-4" />
              {campaign.campanha.sorteado === false
                ? 'Sortear Ganhador'
                : 'Informações do Sorteio'}
            </Button>
          </div>
        </div>

        {/* Lado direito com abas */}
        <div className="w-full lg:w-2/3">
          <Tabs defaultValue="info" className="flex h-full w-full flex-col">
            <TabsList
              className={`mb-4 flex w-full max-w-full flex-wrap justify-between gap-1 px-2 sm:gap-2 sm:px-4 lg:flex-nowrap lg:justify-start`}
            >
              <TabsTrigger
                value="info"
                className="flex items-center justify-center px-2 py-1 text-[10px] sm:px-3 sm:py-2 sm:text-xs lg:min-w-[120px] lg:flex-1 lg:text-sm"
              >
                <BookText className="mr-1 hidden h-4 w-4 sm:inline" />
                <span>
                  <span className="inline sm:hidden">Info</span>
                  <span className="hidden sm:inline">Informações</span>
                </span>
              </TabsTrigger>

              <TabsTrigger
                value="finance"
                className="flex items-center justify-center px-2 py-1 text-[10px] sm:px-3 sm:py-2 sm:text-xs lg:min-w-[120px] lg:flex-1 lg:text-sm"
              >
                <DollarSign className="mr-1 hidden h-4 w-4 sm:inline" />
                Finanças
              </TabsTrigger>

              <TabsTrigger
                value="awarded_ticket"
                className="flex items-center justify-center px-2 py-1 text-[10px] sm:px-3 sm:py-2 sm:text-xs lg:min-w-[120px] lg:flex-1 lg:text-sm"
              >
                <Ticket className="mr-1 hidden h-4 w-4 sm:inline" />
                <span>
                  <span className="inline sm:hidden">Títulos</span>
                  <span className="hidden sm:inline">Títulos Premiados</span>
                </span>
              </TabsTrigger>

              <TabsTrigger
                value="awards"
                className="flex items-center justify-center px-2 py-1 text-[10px] sm:px-3 sm:py-2 sm:text-xs lg:min-w-[120px] lg:flex-1 lg:text-sm"
              >
                <Gift className="mr-1 hidden h-4 w-4 sm:inline" />
                Prêmios
              </TabsTrigger>

              <TabsTrigger
                value="promotion"
                className="flex items-center justify-center px-2 py-1 text-[10px] sm:px-3 sm:py-2 sm:text-xs lg:min-w-[120px] lg:flex-1 lg:text-sm"
              >
                <Tag className="mr-1 hidden h-4 w-4 sm:inline" />
                <span>
                  <span className="inline sm:hidden">Promo</span>
                  <span className="hidden sm:inline">Promoções</span>
                </span>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="info">
              <Card>
                <CardHeader className="flex items-center justify-between">
                  <span className="text-lg font-bold">Gerenciar</span>
                  <StatusToggle
                    active={isActive}
                    onToggle={() => toggleStatusMutation.mutate(!isActive)}
                    disabled={
                      campaign.campanha.sorteado ||
                      allPurchesedNumbers.length > 0
                    }
                    sorteada={campaign.campanha.sorteado}
                  />
                </CardHeader>
                <CardContent className="space-y-4">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="titulo">Título</Label>
                        <Input
                          id="titulo"
                          disabled={campaign.campanha.sorteado}
                          {...register('titulo')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="descricao">Descrição</Label>
                        <Textarea
                          id="descricao"
                          disabled={campaign.campanha.sorteado}
                          {...register('descricao')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="dt_sorteio">Data do Sorteio</Label>
                        <Input
                          id="dt_sorteio"
                          type="datetime-local"
                          disabled={campaign.campanha.sorteado}
                          {...register('dt_sorteio')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="titulo_valor">Valor do Título</Label>
                        <Input
                          id="titulo_valor"
                          type="text"
                          inputMode="decimal"
                          disabled={campaign.campanha.sorteado}
                          defaultValue={campaign.campanha.titulo_Valor || ''}
                          {...register('titulo_valor')}
                          onChange={(e) => {
                            const raw = e.target.value
                            const formatted = raw.replace(/[^0-9,]/g, '')
                            setValue('titulo_valor', formatted, {
                              shouldDirty: true,
                            })
                            e.target.value = formatted
                          }}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="titulo_quantidade">
                          Quantidade Total
                        </Label>
                        <Input
                          id="titulo_quantidade"
                          type="number"
                          min="1"
                          disabled={campaign.campanha.sorteado}
                          onChange={(e) =>
                            handleNumberInput(e, 'titulo_quantidade')
                          }
                          defaultValue={
                            campaign.campanha.titulo_Quantidade || ''
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="titulo_min">Quantidade Mínima</Label>
                        <Input
                          id="titulo_min"
                          type="number"
                          min="1"
                          disabled={campaign.campanha.sorteado}
                          onChange={(e) => handleNumberInput(e, 'titulo_min')}
                          defaultValue={campaign.campanha.titulo_Min || ''}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="titulo_max">Quantidade Máxima</Label>
                        <Input
                          id="titulo_max"
                          type="number"
                          min="1"
                          disabled={campaign.campanha.sorteado}
                          onChange={(e) => handleNumberInput(e, 'titulo_max')}
                          defaultValue={campaign.campanha.titulo_Max || ''}
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label>Prazo</Label>
                          <div
                            className="relative"
                            onMouseEnter={handleShowTooltip}
                            onMouseLeave={handleHideTooltip}
                            onClick={handleTooltipClick}
                          >
                            <Info className="h-4 w-4 cursor-pointer text-gray-500" />
                            {showPrazoTooltip && (
                              <div
                                className="absolute left-full z-10 mt-2 ml-2 w-64 rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-lg"
                                onMouseEnter={() => {
                                  if (tooltipTimeoutRef.current) {
                                    clearTimeout(tooltipTimeoutRef.current)
                                  }
                                }}
                                onMouseLeave={handleHideTooltip}
                              >
                                <p className="font-medium text-gray-900">
                                  Sobre o Prazo
                                </p>
                                <p className="mt-1 text-gray-700">
                                  O prazo selecionado define o tempo que os
                                  participantes terão para realizar o pagamento
                                  após a compra da rifa. Escolha um período que
                                  dê tempo suficiente para o pagamento ser
                                  confirmado.
                                </p>
                                <p className="mt-2 text-xs text-gray-500">
                                  Exemplo: 7 dias significa que o participante
                                  terá 1 semana para pagar.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        <Select
                          disabled={campaign.campanha.sorteado}
                          onValueChange={(value) =>
                            setValue('prazo_id', Number(value), {
                              shouldDirty: true,
                            })
                          }
                          value={watch('prazo_id')?.toString() || ''}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Selecione um prazo" />
                          </SelectTrigger>
                          <SelectContent>
                            {prazoOptions.map((option) => (
                              <SelectItem
                                key={option.id}
                                value={option.id.toString()}
                              >
                                {option.descricao}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Modo</Label>
                        <Select
                          disabled={campaign.campanha.sorteado}
                          onValueChange={(value) =>
                            setValue('modo_id', Number(value), {
                              shouldDirty: true,
                            })
                          }
                          value={watch('modo_id')?.toString() || ''}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Selecione um modo" />
                          </SelectTrigger>
                          <SelectContent>
                            {modoOptions.map((option) => (
                              <SelectItem
                                key={option.id}
                                value={option.id.toString()}
                              >
                                {option.descricao}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="notificar_grupo_whatsapp"
                            disabled={campaign.campanha.sorteado}
                            {...register('notificar_grupo_whatsapp')}
                          />
                          <Label htmlFor="notificar_grupo_whatsapp">
                            Notificar Grupo no WhatsApp
                          </Label>
                        </div>
                      </div>

                      {watch('notificar_grupo_whatsapp') && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label htmlFor="nome_grupo_whatsapp">
                              Nome do Grupo no WhatsApp
                            </Label>
                            <div
                              className="relative"
                              onMouseEnter={() => setShowGrupoTooltip(true)}
                              onMouseLeave={() =>
                                setTimeout(
                                  () => setShowGrupoTooltip(false),
                                  300,
                                )
                              }
                              onClick={(e) => {
                                e.stopPropagation()
                                setShowGrupoTooltip((prev) => !prev)
                              }}
                            >
                              <Info className="h-4 w-4 cursor-pointer text-gray-500" />
                              {showGrupoTooltip && (
                                <div
                                  className="absolute left-full z-10 mt-2 ml-2 w-72 rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-lg"
                                  onMouseEnter={() => setShowGrupoTooltip(true)}
                                  onMouseLeave={() =>
                                    setTimeout(
                                      () => setShowGrupoTooltip(false),
                                      300,
                                    )
                                  }
                                >
                                  <p className="font-medium text-gray-900">
                                    Nome do Grupo
                                  </p>
                                  <p className="mt-1 text-gray-700">
                                    O nome informado deve ser{' '}
                                    <b>exatamente igual</b> ao nome do grupo no
                                    WhatsApp. Isso garante que a notificação
                                    seja enviada corretamente.
                                    <br />
                                    <br />
                                    Certifique-se também de adicionar o número{' '}
                                    <strong>(19) 98992-4246</strong> como
                                    administrador do grupo.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          <Input
                            id="nome_grupo_whatsapp"
                            disabled={campaign.campanha.sorteado}
                            {...register('nome_grupo_whatsapp')}
                          />
                        </div>
                      )}
                    </div>

                    {!campaign.campanha.sorteado && (
                      <div className="mt-6 grid gap-4">
                        <Button
                          type="submit"
                          disabled={
                            !isDirty || updateCampaignMutation.isPending
                          }
                          className="flex-1"
                        >
                          <Save className="mr-2 h-4 w-4" />
                          Salvar Alterações
                        </Button>

                        <Dialog
                          open={openDeleteDialog}
                          onOpenChange={setOpenDeleteDialog}
                        >
                          <DialogTrigger asChild>
                            <Button variant="destructive" className="flex-1">
                              <Trash className="mr-2 h-4 w-4" />
                              Excluir Campanha
                            </Button>
                          </DialogTrigger>

                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>
                                Tem certeza que deseja excluir esta campanha?
                              </DialogTitle>
                            </DialogHeader>
                            <p className="text-muted-foreground text-sm">
                              Esta ação não poderá ser desfeita. Todos os dados
                              relacionados serão permanentemente removidos.
                            </p>
                            <DialogFooter className="flex justify-end gap-2 pt-4">
                              <Button
                                variant="outline"
                                onClick={() => setOpenDeleteDialog(false)}
                              >
                                Cancelar
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={() => {
                                  deleteCampaignMutation.mutate()
                                  setOpenDeleteDialog(false)
                                }}
                                disabled={updateCampaignMutation.isPending}
                              >
                                Confirmar exclusão
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="finance">
              <Card>
                <CardHeader className="text-lg font-bold">Finanças</CardHeader>
                <CardContent>
                  <FinanceTab
                    campanhaId={Number(rifaId)}
                    tituloQuantidade={campaign.campanha.titulo_Quantidade}
                  />
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="awarded_ticket">
              <Card>
                <CardHeader className="text-lg font-bold">
                  Títulos Premiados
                </CardHeader>
                <CardContent>
                  <TituloPremiadoCRUD campanhaId={Number(rifaId)} />
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="awards">
              <Card>
                <CardHeader className="text-lg font-bold">Prêmios</CardHeader>
                <CardContent>
                  <PremioCRUD campanhaId={Number(rifaId)} />
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="promotion">
              <Card>
                <CardHeader className="text-lg font-bold">Promoções</CardHeader>
                <CardContent>
                  <PromocaoCRUD campanhaId={Number(rifaId)} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
